// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";

// models
import { ICreateLineGroupReq, IUpdateLineGroupReq, ITags } from "@/lib/backend/manufacturing2.generated.types";
import { LINE_GROUP_TAGS } from "@/lib/ManufacturingTags.generated";
import * as BusinessLogic from "@/lib/businessLogic";
import * as Auth from "@/lib/auth";
import LineGroupEditTags from "./LineGroupEditTags";
import { onChangeUpdateTags, type TagUpdateValue } from "@/modules/shared/ui/ReactTableTags";

// subcomponents
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Toggle from "react-toggle";
import { translate } from "@/modules/shared/IntlProviderWrapper";

// backend
import { getBackend } from "@/lib/backend/backend2";

import img_banner from "@/img/banner.png";

// type definitions
type ITagDisplay = {
    name: string;
    id: string;
};

export type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

export type State = {
    external_id: string;
    gannt: boolean;
    hide_in_navigation: boolean;
    microplan: boolean;
    shift_board: boolean;
    stock_forecast: boolean;
    batch_op: boolean;
    subcontracting: boolean;
    weekly_realization: boolean;
    uuid: string;
    title: string;
    weight: number;
    tags: ITags;
    message: string;
    componentTitle: string;
    error: string | null;
    warning: string;
};

/**
 * Form to create new line.
 */
export default class LineGroupCreate extends Component<Props, State> {
    is_edit = false;

    state: State = {
        external_id: "",
        gannt: false,
        hide_in_navigation: false,
        microplan: false,
        shift_board: false,
        stock_forecast: false,
        batch_op: false,
        subcontracting: false,
        weekly_realization: false,
        uuid: "",
        title: "",
        weight: 0,
        tags: {
            gannt: "false",
            hide_in_navigation: "false",
            microplan: "false",
            shift_board: "false",
            stock_forecast: "false",
            batch_op: "false",
            subcontracting: "false",
            weekly_realization: "false",
        },
        message: "",
        componentTitle: translate("Manufacturing.LineGroups.add_new_line_group", "Add New Line Group"),
        error: "",
        warning: "",
    };

    /**
     * Basic form validation.
     */
    validateShallow() {
        return this.state.title.length > 0 && this.state.external_id.length > 0;
    }

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        let value: string | boolean = target.value;
        const name = target.name;

        // hack for toggle element
        if (Object.keys(LINE_GROUP_TAGS).includes(name)) {
            value = (target as HTMLInputElement).checked;
        }

        // handle line group
        if (name === "line_group") {
            const tags = this.state.tags || {};
            tags.line_group = value.toString();
            this.setState({ tags });
        } else {
            // update new datasource placeholder
            this.setState(prev_state => ({
                ...prev_state,
                [name]: value,
            }));
        }

        // handle tags
        if (Object.keys(LINE_GROUP_TAGS).includes(name)) {
            const tags = this.state.tags || {};
            tags[name] = value.toString();
            this.setState({ tags });
        }
    };

    /**
     * Handling submission of the form. Basic checks and saves with error handling.
     */
    handleSubmit = async () => {
        this.setState({ error: null });
        try {
            if (this.validateShallow()) {
                // insert a new object or update
                if (this.state.uuid === "") {
                    let lg: ICreateLineGroupReq = {
                        external_id: this.state.external_id,
                        title: this.state.title,
                        tags: this.state.tags,
                        weight: this.state.weight,
                    };
                    const new_line_group = await getBackend().manufacturing.createLineGroup(lg);
                    // force refresh cache to get new line group tags
                    await Auth.reloadUserProfile();
                    await BusinessLogic.forceManufacturingCacheRefresh();
                    this.props.history.push("/digital-twin/resources/line_groups/" + new_line_group.uuid);
                } else {
                    // update current object
                    let lg: IUpdateLineGroupReq = {
                        linegroup_uuid: this.state.uuid,
                        external_id: this.state.external_id,
                        title: this.state.title,
                        tags: this.state.tags,
                        weight: this.state.weight,
                    };
                    await getBackend().manufacturing.updateLineGroup(lg);
                    // force refresh cache to get new line group tags
                    await BusinessLogic.forceManufacturingCacheRefresh();
                    this.props.history.goBack();
                }
            } else {
                this.setState({
                    warning: translate(
                        "Manufacturing.LineGroups.cannot_submit_missing_values",
                        "Cannot submit a form with missing values"
                    ),
                });
            }
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    };

    renderTagField(tag: ITagDisplay) {
        return (
            <div className="form-group" key={tag.id}>
                <label>{tag.name}</label>
                <br />
                <Toggle name={tag.id} checked={this.state[tag.id]} onChange={this.handleInputChange} />
            </div>
        );
    }

    onChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    render() {
        return (
            <div key="ds_new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    <h2 className="mb-4">{this.state.componentTitle}</h2>
                                    <ErrorComponent msg={this.state.error} type="error" />
                                    <ErrorComponent msg={this.state.warning} type="warning" />
                                    <div className="tab-content">
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.title" defaultMessage="Title" />:
                                            </label>
                                            <input
                                                name="title"
                                                type="text"
                                                className="form-control"
                                                value={this.state.title}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage
                                                    id="common.external_id"
                                                    defaultMessage="External ID"
                                                />
                                                :
                                            </label>
                                            <input
                                                name="external_id"
                                                type="text"
                                                disabled={this.is_edit ? true : false}
                                                className="form-control"
                                                value={this.state.external_id}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.weight" defaultMessage="Weight" />:
                                            </label>
                                            <input
                                                name="weight"
                                                type="text"
                                                className="form-control"
                                                value={this.state.weight}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>
                                    </div>
                                    <LineGroupEditTags tags={this.state.tags} onChangeCell={this.onChangeCell} />
                                    <div className="button-list button-list-entity-edit order-last ml-auto">
                                        <button className="btn btn-primary" onClick={this.handleSubmit}>
                                            <FormattedMessage id="common.save" defaultMessage="Save" />
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => this.props.history.goBack()}
                                        >
                                            <FormattedMessage id="common.back" defaultMessage="Back" />
                                        </button>
                                    </div>
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}
